import { Injectable } from '@angular/core';
import * as moment from 'moment';
import * as jsPDF from 'jspdf';
import {LoaderService} from "@catloader/loader-service/services/loader.service";

@Injectable()
export class SvgExportService {

  static readonly EXPORT_SIZE_DPI = 300;
  static readonly INTERMEDIATE_IMAGE_DPI = 600;
  static readonly MM_PER_INCH = 25.4;

  // Valid paper sizes
  static readonly PAPERSIZE_A3 = 'a3';
  static readonly PAPERSIZE_A4 = 'a4';

  // Valid paper orientations
  static readonly ORIENTATION_LANDSCAPE = 'l';
  static readonly ORIENTATION_PORTRAIT = 'p';

  // Compression options
  static readonly COMPRESSION_NONE = 'NONE';
  static readonly COMPRESSION_FAST = 'FAST';
  static readonly COMPRESSION_MEDIUM = 'MEDIUM';
  static readonly COMPRESSION_SLOW = 'SLOW';

  // Paper sizes in mm
  private paperSizes = {
    'a3': {
      width: 297,
      height: 420
    },
    'a4': {
      width: 210,
      height: 297
    }
  };

  constructor(private loaderService: LoaderService) {
  }

  /**
   * Exports an svg image to pdf.
   *
   * The svg is converted to a png first using a canvas.
   *
   * @param svgElem the svg element (native)
   * @param canvasElem a canvas element used to render intermediate results
   * @param pngContainerDivElem a container to insert an intermediate image
   * @param title the document title
   * @param subtitle
   * @param filename the filename
   * @param paperOrientation the paper orientation
   * @param paperSize the paper size
   * @param scaleToFit whether the image size is scaled up to the page size
   * @param compression applicable compression value (see class constants)
   */
  exportSvgToPdf(svgElem, canvasElem, pngContainerDivElem,
                 title, subtitle, filename, paperOrientation, paperSize,
                 scaleToFit, compression) {
    this.loaderService.show();
    const svgString = new XMLSerializer().serializeToString(svgElem);

    const width = paperOrientation === SvgExportService.ORIENTATION_PORTRAIT ? 'width' : 'height';
    const height = paperOrientation === SvgExportService.ORIENTATION_PORTRAIT ? 'height' : 'width';

    let imageWidth = svgElem.width.baseVal.value;
    let imageHeight = svgElem.height.baseVal.value;

    const imageRatio = imageWidth / imageHeight;
    const paperWidth = this.paperSizes[paperSize][width];
    const paperHeight = this.paperSizes[paperSize][height];
    const paperRatio = paperWidth / paperHeight;

    let imageWidthMm = imageWidth * SvgExportService.MM_PER_INCH / SvgExportService.EXPORT_SIZE_DPI;
    let imageHeightMm = imageHeight * SvgExportService.MM_PER_INCH / SvgExportService.EXPORT_SIZE_DPI;

    // Scale to fit if desired or if the image is larger than the paper
    const maxImageWidthMm = paperWidth - 10;
    const maxImageHeightMm = paperHeight - 10;
    if (scaleToFit
        || imageWidthMm > maxImageWidthMm
        || imageHeightMm > maxImageHeightMm) {
      imageWidthMm = maxImageWidthMm;
      imageHeightMm = maxImageWidthMm / imageRatio;
      if (imageRatio < paperRatio) {
        imageHeightMm = maxImageHeightMm;
        imageWidthMm = imageRatio * imageHeightMm;
      }
    }

    let canvasWidth = imageWidthMm * SvgExportService.INTERMEDIATE_IMAGE_DPI / SvgExportService.MM_PER_INCH;
    let canvasHeight = imageHeightMm * SvgExportService.INTERMEDIATE_IMAGE_DPI / SvgExportService.MM_PER_INCH;

    canvasElem.width = canvasWidth;
    canvasElem.height = canvasHeight;

    const ctx = canvasElem.getContext("2d");
    const DOMURL = window.URL || window['webkitURL'] || window;
    const img = new Image();
    const svg = new Blob([svgString], {type: "image/svg+xml;charset=utf-8"});
    const url = DOMURL.createObjectURL(svg);

    const that = this;
    img.onload = function () {
      try {
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        const png = canvasElem.toDataURL("image/png");
        pngContainerDivElem.innerHTML = '<img src="' + png + '"/>';
        DOMURL.revokeObjectURL(png);

        // Using millimeters for units
        const doc = new jsPDF(paperOrientation, 'mm', paperSize, true);

        doc.addImage(png, 'PNG', 5, 5, imageWidthMm, imageHeightMm, '', compression);
        doc.setDrawColor(128, 128, 128);
        doc.rect(4, 4, paperWidth - 8, paperHeight - 8);
        doc.setFillColor(255, 255, 255);
        doc.rect(paperWidth - 85, paperHeight - 21, 81, 17, 'FD');

        doc.setFontSize(13);
        doc.setTextColor(64, 64, 64);
        doc.text(paperWidth - 82, paperHeight - 15, title);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(paperWidth - 82, paperHeight - 10, subtitle);
        doc.text(paperWidth - 82, paperHeight - 6, 'Created ' + moment().format('MMMM Do YYYY'));

        doc.save(filename);

        ctx.clearRect(0, 0, canvasElem.width, canvasElem.height);
      } finally {
        that.loaderService.hide();
      }
    };

    img.src = url;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/core/services/svg-export/svg-export.service.ts